import { useEffect, useState } from 'react'
import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { RegistrationDocument } from 'feature/registration/types'
import {
  getRegistrationLkmDocuments,
  setRegistrationLkmDocuments,
} from 'feature/registration/storage/registrationLkmStorage'
import { DragDropInput } from 'widget/DragDropFile'
import {
  MAX_DOCUMENTS_COUNT,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_BYTES_CUSTOMER,
  ALLOWED_CUSTOMER_MIME_TYPES,
  ALLOWED_CUSTOMER_EXTENSIONS,
  ALLOWED_CUSTOMER_FORMATS,
  FORBIDDEN_VIDEO_TYPES,
} from './config/documentsConfig'
import {
  useDeleteRegistrationDocumentMutation,
  useUploadRegistrationDocumentMutation,
} from 'feature/registration/api/registrationLkmApi'

type Props = {
  helperText?: string
  label?: string
  // Кастомная функция загрузки — если не передана, используется стандартный fitter endpoint
  onUploadFile?: (file: File) => Promise<{ uid: string; previewUrl: string }>
  // Колбэк с актуальным списком UUID после любого изменения файлов
  onChange?: (uuids: string[]) => void
  // Колбэк с полным списком файлов (для персистентности)
  onFilesChange?: (files: orderFormSpace.Images[]) => void
  // Начальный список файлов (для восстановления после ремонтирования)
  initialFiles?: orderFormSpace.Images[]
}

export function DocumentsUploadInput({
  helperText,
  label,
  onUploadFile,
  onChange,
  onFilesChange,
  initialFiles,
}: Props) {
  const [uploadDocument] = useUploadRegistrationDocumentMutation()
  const [deleteDocument] = useDeleteRegistrationDocumentMutation()

  const [fileList, setFileList] = useState<orderFormSpace.Images[]>(() => {
    if (onUploadFile) return initialFiles ?? []
    const initialDocuments = getRegistrationLkmDocuments()
    return initialDocuments.map((document, index) => ({
      id: document.uid || `initial-doc-${index}`,
      name: document.name || `Документ ${index + 1}`,
      serverUid: document.uid,
      previewUrl: '',
      size: document.size || '',
      mimeType: document.mimeType || '',
    }))
  })

  useEffect(() => {
    if (onUploadFile) {
      const uuids = fileList
        .filter(f => f.serverUid)
        .map(f => f.serverUid as string)
      onChange?.(uuids)
      onFilesChange?.(fileList)
      return
    }

    const uploadedDocuments = fileList.reduce<RegistrationDocument[]>(
      (acc, item) => {
        if (item.serverUid) {
          acc.push({
            uid: item.serverUid,
            name: item.name,
            size: item.size || '',
            mimeType: item.mimeType || '',
          })
        }
        return acc
      },
      [],
    )
    setRegistrationLkmDocuments(uploadedDocuments)
  }, [fileList])

  const handleUploadDocument = async (file: File) => {
    if (onUploadFile) {
      return onUploadFile(file)
    }
    const result = await uploadDocument(file).unwrap()
    return {
      uid: result.documentId,
      previewUrl: '',
    }
  }

  const handleRemoveDocument = async (file: orderFormSpace.Images) => {
    if (onUploadFile) return
    if (file.serverUid) {
      await deleteDocument(file.serverUid).unwrap()
    }
  }

  // ОС не всегда отдаёт mime-тип (тогда file.type пустой и браузер отправит
  // application/octet-stream), поэтому проверяем ещё и расширение
  const isAllowedCustomerFile = (file: File) => {
    if (ALLOWED_CUSTOMER_MIME_TYPES.includes(file.type)) return true
    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    return ALLOWED_CUSTOMER_EXTENSIONS.includes(extension)
  }

  const validateFile = (file: File) => {
    if (onUploadFile) {
      if (!isAllowedCustomerFile(file)) {
        return {
          isError: true,
          message: 'Разрешены только PDF, DOCX, PNG, JPEG.',
        }
      }
      if (file.size > MAX_FILE_SIZE_BYTES_CUSTOMER) {
        return { isError: true, message: 'Максимальный размер файла 1 МБ.' }
      }
    } else {
      if (FORBIDDEN_VIDEO_TYPES.includes(file.type)) {
        return { isError: true, message: 'Видео форматы не поддерживаются.' }
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return { isError: true, message: 'Максимальный размер файла 10 МБ.' }
      }
    }

    return { isError: false, message: '' }
  }

  return (
    <DragDropInput
      labelName={label ?? ''}
      helperTitle='Загрузите файл'
      allowedFormats={onUploadFile ? ALLOWED_CUSTOMER_FORMATS : '*'}
      limit={MAX_DOCUMENTS_COUNT}
      errorText={
        onUploadFile
          ? 'Разрешены только PDF, DOCX, PNG, JPEG до 1 МБ'
          : 'Загрузите файл любого формата (кроме видео) до 10 МБ'
      }
      isError={false}
      helperText={helperText}
      fileList={fileList}
      setFileList={setFileList}
      loadFileToApi={handleUploadDocument}
      onRemoveFile={handleRemoveDocument}
      validateFile={validateFile}
    />
  )
}
